import { memo, useMemo, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconUserOutline18 as User,
	IconUserXmarkOutline18 as UserX,
} from "nucleo-ui-outline-18"

import type { AgentLinkedIssue } from "@productlane/lib/src/agent-recommendations"
import {
	Badge,
	Button,
	Input,
	Label,
	LinearIcon,
	Popover,
} from "@productlane/ui"

import { PopoverButton } from "@/components/popover-button"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { getProjectIcon } from "@/lib/projects"
import { useSession } from "@/lib/session"
import { useZeroWorkspace } from "@/lib/zero"
import { assigneeAndAgentOptions } from "../issues/assignee-options"
import { resolveDefaultTeamId } from "../threads/hooks/resolve-default-team-id"
import { useSyncedTeams } from "../threads/hooks/useSelectedTeams"
import { ENGINEERING_COPY, linkedIssueLabel } from "./format"
import { openExternal } from "./use-engineering-issue"

const LINEAR_SETTINGS_PATH = "/settings/integrations/linear"

/** The filed issue in the top bar: "ENG-512 · Todo" and a way into Linear. */
export const LinkedIssueBar = memo(
	({
		issue,
		statusName,
	}: {
		issue: AgentLinkedIssue
		// The synced Issue row's status, which follows Linear after filing.
		statusName?: string | null
	}) => (
		<div className="flex flex-none items-center gap-2">
			<Badge data-testid="engineering-issue-badge" Icon={LinearIcon}>
				{linkedIssueLabel(issue.identifier, statusName ?? issue.statusName)}
			</Badge>
			<Button
				variant="outline"
				Icon={LinearIcon}
				onClick={() => openExternal(issue.url)}
			>
				{ENGINEERING_COPY.openInLinear}
			</Button>
		</div>
	),
)

export const CreateIssueButton = memo(
	({
		defaultTitle,
		disabled,
		busy,
		onCreate,
	}: {
		defaultTitle: string
		disabled?: boolean
		busy: boolean
		onCreate: (input: {
			teamId: string
			title: string
			assigneeId?: string
		}) => Promise<boolean>
	}) => {
		const navigate = useNavigate()
		const workspace = useZeroWorkspace()
		const { session } = useSession()
		const teamsQuery = api.linear.teams.useQuery(undefined, {
			cacheTime: Infinity,
			staleTime: Infinity,
		})
		const teams = useSyncedTeams()
		const defaultTeamId = resolveDefaultTeamId(
			workspace?.defaultLinearTeamId,
			teams,
		)
		const [open, setOpen] = useState(false)
		const [picked, setPicked] = useState<string | null>(null)
		const [assigneeId, setAssigneeId] = useState<string | null>(null)
		const [title, setTitle] = useState(defaultTitle)
		// A team hidden in Linear settings since it was picked falls back to the default.
		const teamId =
			picked && teams.some((team) => team.id === picked)
				? picked
				: defaultTeamId

		const teamOptions = useMemo(
			() =>
				teams.map(({ icon, color, id, name }) => {
					const Icon = getProjectIcon(icon)
					return {
						label: (
							<div className="flex items-center">
								<Icon className="flex-none" style={{ color }} />
								<span className="ml-2">{name}</span>
							</div>
						),
						value: id,
					}
				}),
			[teams],
		)

		const { data: members } = api.linear.members.useQuery(
			{ teamId: teamId ?? "" },
			{ cacheTime: Infinity, staleTime: Infinity, enabled: open && !!teamId },
		)
		// Members only: delegating to a Linear agent stays in the full issue dialog.
		const assigneeOptions = useMemo(
			() =>
				assigneeAndAgentOptions({
					members,
					agents: [],
					selfEmail: session?.email,
					assigneeId,
				}),
			[members, session?.email, assigneeId],
		)

		const teamsLoaded = !teamsQuery.isLoading
		const canSubmit = !!teamId && title.trim().length > 0 && !busy

		const onOpenChange = (next: boolean) => {
			if (next && teamsLoaded && teams.length === 0) {
				toast.info({
					title: ENGINEERING_COPY.noTeamsToast,
					icon: LinearIcon,
					action: {
						label: ENGINEERING_COPY.openSettings,
						callback: () => void navigate({ href: LINEAR_SETTINGS_PATH }),
					},
				})
				return
			}
			setOpen(next)
		}

		const submit = async () => {
			if (!teamId || !canSubmit) return
			const ok = await onCreate({
				teamId,
				title: title.trim(),
				assigneeId: assigneeId ?? undefined,
			})
			if (ok) setOpen(false)
		}

		return (
			<Popover
				open={open}
				onOpenChange={onOpenChange}
				align="end"
				side="bottom"
				className="w-80 p-3"
				content={
					<form
						data-testid="engineering-issue-panel"
						className="flex flex-col gap-3"
						onSubmit={(event) => {
							event.preventDefault()
							void submit()
						}}
					>
						<div className="flex flex-col gap-1.5">
							<Label>{ENGINEERING_COPY.teamLabel}</Label>
							<PopoverButton
								variant="outline"
								className="w-full justify-start"
								data={teamOptions}
								selected={teamId}
								isLoading={!teamsLoaded}
								placeholder="Select team..."
								onChange={(value: string | null) => {
									if (!value || value === teamId) return
									setPicked(value)
									// Members belong to a team, so a new team clears the assignee.
									setAssigneeId(null)
								}}
							/>
						</div>
						<div className="flex flex-col gap-1.5">
							<Label>{ENGINEERING_COPY.assigneeLabel}</Label>
							<PopoverButton
								// Shown on the trigger while nobody is assigned.
								name="No assignee"
								variant="outline"
								className="w-full justify-start"
								data={assigneeOptions}
								selected={assigneeId ?? undefined}
								isLoading={!!teamId && !members}
								placeholder="Assign to..."
								icon={User}
								resetOption="No assignee"
								resetIcon={UserX}
								onChange={(value: string | null) => setAssigneeId(value)}
							/>
						</div>
						<Input
							name="engineering-issue-title"
							label={ENGINEERING_COPY.titleLabel}
							value={title}
							maxLength={255}
							onChange={(event) => setTitle(event.target.value)}
						/>
						<Button
							type="submit"
							Icon={LinearIcon}
							isLoading={busy}
							disabled={!canSubmit}
						>
							{ENGINEERING_COPY.createButton}
						</Button>
					</form>
				}
			>
				<Button Icon={LinearIcon} disabled={disabled || busy}>
					{ENGINEERING_COPY.acceptButton}
				</Button>
			</Popover>
		)
	},
)
